import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import Animated from 'react-native-reanimated';
import { FlashList } from '@shopify/flash-list';
import { View, TouchableOpacity } from 'react-native';
import Spacer from 'design-system/Spacer';
import Indicator from 'design-system/Indicator';
import IconPlus from 'design-system/icons/IconPlus';
import IconFilterBold from 'design-system/icons/IconFilterBold';
import ReanimatedStackHeader from 'design-system/hoc/ReanimatedStackHeader';
import { AppView, IconEdit, withAnimatedNavigationHeader } from 'design-system';
import { rem, hitSlop, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { extractKeyFromKey } from 'utils/flatListHelpers';
import { AddToWatchListFlow } from 'utils/types/navigation';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { AssetFilterCategory } from '../types';
import useWatchList from '../hooks/useWatchList';
import EmptyState from '../components/EmptyState';
import TrendingStock from '../components/TrendingStock';
import Header from '../components/CategoryHeaderComponent';
import { FilterAssetsModal } from '../components/FilterAssetsModal';
import { useGetWatchListInfoForWatchListId, useWatchListAssetsCacheForWatchList } from '../hooks/selectors';
import { isWeb } from '../../../constants';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const HeaderRight = ({ id, openFilterModal }) => {
    const navigation = useMainStackNavigation();
    const watchList = useGetWatchListInfoForWatchListId(id);
    const onAddPress = () => {
        navigation.navigateDeprecated('InvestSearchStock', { watchListId: id });
    };
    const onEditPress = () => {
        if (watchList) {
            navigation.navigateDeprecated('InvestCreateWatchList', {
                id,
                createParams: {
                    watchList,
                    flow: AddToWatchListFlow.UpdateWatchList,
                },
            });
        }
    };
    const onFilterPress = () => {
        openFilterModal?.();
    };
    const colors = useColors();
    const styles = useStyles(styleSet);
    return (<View style={styles.headerRightContainer}>
      <TouchableOpacity onPress={onFilterPress} hitSlop={hitSlop}>
        <IconFilterBold color={colors.text.primary}/>
      </TouchableOpacity>
      <Spacer w={16}/>
      <TouchableOpacity onPress={onEditPress} hitSlop={hitSlop}>
        <IconEdit color={colors.text.primary}/>
      </TouchableOpacity>
      <Spacer w={16}/>
      <TouchableOpacity onPress={onAddPress} hitSlop={hitSlop}>
        <IconPlus color={colors.text.primary}/>
      </TouchableOpacity>
    </View>);
};
const WatchListScreen = withAnimatedNavigationHeader(({ route, scrollY, navigation, scrollHandler }) => {
    const id = route.params?.id;
    const [state, setState] = useState({
        selectedFilter: AssetFilterCategory.NAME,
    });
    const assets = useWatchListAssetsCacheForWatchList(id, state.selectedFilter);
    const assetsIndexLength = useMemo(() => (assets?.length || 0) - 1, [assets.length]);
    const watchList = useGetWatchListInfoForWatchListId(id);
    const [loading, , , , , getWatchList] = useWatchList();
    useLayoutEffect(() => {
        const headerTitleWidth = watchList?.name ? rem(150) : '100%';
        navigation.setOptions({
            headerTitle: () => watchList?.name ? (<ReanimatedStackHeader title={watchList?.name} scrollY={scrollY} width={headerTitleWidth}/>) : null,
        });
    }, [scrollY, watchList?.name]);
    useLayoutEffect(() => {
        const onSelectFilter = (filter) => {
            setState((oldState) => ({
                ...oldState,
                selectedFilter: filter,
            }));
        };
        const openFilterModal = () => {
            FilterAssetsModal.show(state.selectedFilter, onSelectFilter);
        };
        navigation.setOptions({
            headerRight: () => <HeaderRight id={id} openFilterModal={openFilterModal}/>,
        });
    }, [id, state.selectedFilter]);
    const getWatchListDetails = useCallback(() => {
        getWatchList(id);
    }, [getWatchList, id]);
    useEffect(() => {
        getWatchListDetails();
    }, [getWatchListDetails]);
    const navigateToSearchStocksScreen = () => {
        navigation.navigateDeprecated('InvestSearchStock', { watchListId: id });
    };
    const renderItem = useCallback(({ item, index }) => (<TrendingStock assetId={item.id} isFirstItem={index === 0} isLastItem={index === assetsIndexLength}/>), [assetsIndexLength]);
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
        <AnimatedFlashList data={assets} renderItem={renderItem} onScroll={scrollHandler} scrollEventThrottle={16} keyboardDismissMode="on-drag" keyExtractor={extractKeyFromKey('id')} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle} ListEmptyComponent={loading ? null : <EmptyState watchlist onPress={navigateToSearchStocksScreen}/>} ListHeaderComponent={<Header total={assets.length} name={watchList?.name || ''} emoji={watchList?.emoji || ''}/>}/>
        {loading && !assets.length ? <Indicator dark style={styles.absView}/> : null}
      </AppView>);
}, ({ colors }) => ({
    hasCustomTitle: true,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    listEmptyContainer: {
        paddingVertical: rem(24),
        margin: rem(16),
        borderRadius: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    searchBtnContainer: {
        alignItems: 'center',
    },
    btn: {
        alignSelf: 'center',
        paddingHorizontal: rem(22),
    },
    headerRightContainer: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    absView: {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        position: 'absolute',
    },
}));
export default WatchListScreen;
